import type { CommentCategoryOutputV1 } from '@/sdk/model/CommentCategoryOutputV1';
import type { LabelOutputV1 } from '@/sdk/model/LabelOutputV1';
import type { NumericDescriptionOutputV1 } from '@/sdk/model/NumericDescriptionOutputV1';
import type { Maybe } from '@/utilities.types';

export enum UserDefinedContextType {
  Toggle = 'Toggle',
  Label = 'Label',
  Numeric = 'Numeric',
  Text = 'Text',
}

export const isUserDefinedContextType = (type: string): type is UserDefinedContextType => {
  return Object.values(UserDefinedContextType).includes(type as UserDefinedContextType);
};

export type UserDefinedContextConfigErrors = Record<string, Maybe<string>>;

// Base interface with common fields
export interface UserDefinedContextConfigBase {
  key: string;
  type: UserDefinedContextType;
  name: string;
  icon: string;
  errors?: UserDefinedContextConfigErrors;
}

// Interface for Boolean and Label context types
export interface UserDefinedContextLabelsConfig extends UserDefinedContextConfigBase {
  type: UserDefinedContextType.Toggle | UserDefinedContextType.Label;
  categoryId: string;
  labels: LabelOutputV1[];
}

// Interface for Numeric context type
export interface UserDefinedContextNumericConfig extends UserDefinedContextConfigBase {
  type: UserDefinedContextType.Numeric;
  numericDescriptionId: string;
  numericDescription: NumericDescriptionOutputV1;
}

// Interface for Text context type
export interface UserDefinedContextTextConfig extends UserDefinedContextConfigBase {
  type: UserDefinedContextType.Text;
  commentCategoryId: string;
  commentCategory: CommentCategoryOutputV1;
}

// Union type for all context config types
export type UserDefinedContextConfigType =
  | UserDefinedContextLabelsConfig
  | UserDefinedContextNumericConfig
  | UserDefinedContextTextConfig;

// Type guards for safe type checking
export const isToggleContextConfig = (config: UserDefinedContextConfigType): config is UserDefinedContextLabelsConfig =>
  config.type === UserDefinedContextType.Toggle;

export const isLabelContextConfig = (config: UserDefinedContextConfigType): config is UserDefinedContextLabelsConfig =>
  config.type === UserDefinedContextType.Label;

export const isLabelBasedContextConfig = (
  config: UserDefinedContextConfigType,
): config is UserDefinedContextLabelsConfig => isToggleContextConfig(config) || isLabelContextConfig(config);

export const isNumericContextConfig = (
  config: UserDefinedContextConfigType,
): config is UserDefinedContextNumericConfig => config.type === UserDefinedContextType.Numeric;

export const isTextContextConfig = (config: UserDefinedContextConfigType): config is UserDefinedContextTextConfig =>
  config.type === UserDefinedContextType.Text;
